import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, UserCheck, CircleCheck, XCircle, X, Zap, ArrowLeft, Mail, Filter } from 'lucide-react';
import { heatOf, isOpen, type Ticket } from '@/lib/mock';
import { useMacros } from '@/lib/macros';
import { untilLabel } from '@/lib/useNow';
import { cn } from '@/lib/utils';

import { useSearchParams, useNavigate } from 'react-router-dom';

type ViewId = 'all-open' | 'my' | 'my-p1' | 'email' | 'breaching' | 'breached' | 'unassigned' | 'in-progress' | 'on-hold' | 'completed' | 'escalated' | 'pending-approvals';
const VIEWS: { id: ViewId; label: string }[] = [
  { id: 'all-open', label: 'All open' },
  { id: 'my', label: 'My queue' },
  { id: 'email', label: 'Email Tickets' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'in-progress', label: 'In Progress' },
  { id: 'pending-approvals', label: 'Pending Approvals' },
  { id: 'on-hold', label: 'On Hold' },
  { id: 'completed', label: 'Completed' },
  { id: 'escalated', label: 'Escalated' },
  { id: 'breaching', label: 'Breaching soon' },
  { id: 'breached', label: 'Breached' },
];

function inView(t: Ticket, view: ViewId, now: number, me: string, myUserId?: string): boolean {
  const isMine = (myUserId && t.assigneeId === myUserId) || t.assigneeId === me || t.assignee === me;
  switch (view) {
    case 'my': return isMine && isOpen(t);
    case 'my-p1': return isMine && t.priority === 'P1' && isOpen(t);
    case 'email': return t.source === 'Email' && isOpen(t);
    case 'in-progress': return isMine && t.state === 'In Progress';
    case 'on-hold': return isMine && (t.state === 'On Hold' || t.state === 'Pending Vendor' || t.state === 'Pending Customer');
    case 'completed': return isMine && (t.state === 'Resolved' || t.state === 'Closed');
    case 'pending-approvals': return (
      t.state === 'Pending Approval' ||
      t.state === 'Pending CAB' ||
      (t as any).approval_status === 'PENDING' ||
      (t as any).approval_status === 'Pending Approval' ||
      !!(t.approvals && t.approvals.some(a => a.status === 'pending'))
    );
    case 'breached': return now > t.slaDueAt && t.state !== 'Resolved' && t.state !== 'Closed';
    case 'unassigned': return (!t.assignee || t.assignee === 'Unassigned' || t.assignee === '—' || !t.assigneeId) && isOpen(t);
    case 'all-open':
    default: return isOpen(t);
  }
}

function formatCreatedDate(timestamp: number) {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(timestamp: number, now = Date.now()): string {
  if (!timestamp) return '';
  const diff = now - timestamp;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function WorkQueuePage({
  tickets,
  now,
  me,
  myUserId,
  onOpenTicket,
  bulkAssign,
  bulkStatus,
  bulkUpdate,
  onBack,
}: {
  tickets: Ticket[];
  now: number;
  me: string;
  myUserId?: string;
  onOpenTicket: (t: Ticket) => void;
  bulkAssign: (ids: string[], assignee: string | null, assigneeName?: string) => void;
  bulkStatus: (ids: string[], state: string) => void;
  bulkUpdate: (ids: string[], body: string) => void;
  onBack?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialView = (searchParams.get('view') as ViewId) || 'my';
  const [view, setView] = useState<ViewId>(initialView);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'Email' | 'Web' | 'API'>('all');
  const [sortBy, setSortBy] = useState<'created-desc' | 'created-asc' | 'heat'>('created-desc');
  
  // Update URL when view changes
  useEffect(() => {
    if (view !== 'my') {
      setSearchParams({ view });
    } else {
      setSearchParams({});
    }
  }, [view, setSearchParams]);

  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [active, setActive] = useState(0);
  const [macroMenu, setMacroMenu] = useState(false);
  const { macros } = useMacros();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const rows = useMemo(() => {
    return tickets
      .filter((t) => inView(t, view, now, me, myUserId))
      .filter((t) => sourceFilter === 'all' || (t.source || 'Web') === sourceFilter)
      .filter((t) => (q ? `${t.ref} ${t.subject} ${t.team} ${t.assignee ?? ''} ${t.source ?? ''}`.toLowerCase().includes(q.toLowerCase()) : true))
      .sort((a, b) => {
        if (sortBy === 'created-desc') return (b.createdAt || 0) - (a.createdAt || 0);
        if (sortBy === 'created-asc') return (a.createdAt || 0) - (b.createdAt || 0);
        return heatOf(b, now) - heatOf(a, now);
      });
  }, [tickets, view, sourceFilter, q, now, me, myUserId, sortBy]);

  useEffect(() => { setActive(0); setSelected(new Set()); }, [view, q]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // keyboard: j/k or down/up move, x select, enter open, e assign-to-me
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((prev) => {
          const next = Math.min(rows.length - 1, prev + 1);
          const targetEl = document.getElementById(`ticket-row-${rows[next]?.id}`);
          targetEl?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      }
      else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((prev) => {
          const next = Math.max(0, prev - 1);
          const targetEl = document.getElementById(`ticket-row-${rows[next]?.id}`);
          targetEl?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      }
      else if (e.key === 'x') { e.preventDefault(); if (rows[active]) toggle(rows[active].id); }
      else if (e.key === 'Enter') { e.preventDefault(); if (rows[active]) onOpenTicket(rows[active]); }
      else if (e.key === 'e') { e.preventDefault(); if (rows[active]) bulkAssign([rows[active].id], myUserId || me, me); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, active, me, myUserId, bulkAssign, onOpenTicket]);

  const ids = [...selected];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" ref={containerRef}>
      <div className="mx-auto flex w-full max-w-[1560px] flex-1 min-h-0 flex-col px-6 py-5 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (onBack) {
                  onBack();
                } else if (window.history.length > 2) {
                  navigate(-1);
                } else {
                  // Fallback if no history
                  navigate('/');
                }
              }}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="font-display text-xl font-medium tracking-tight">Work queue</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-xl border border-input bg-card px-2.5 py-1.5 text-xs text-muted-foreground shadow-sm">
              <Filter className="h-3.5 w-3.5" />
              <select
                value={sourceFilter}
                onChange={(e: any) => setSourceFilter(e.target.value)}
                className="bg-transparent text-xs font-medium text-foreground outline-none cursor-pointer"
              >
                <option value="all">All Sources</option>
                <option value="Email">Email Only</option>
                <option value="Web">Portal Only</option>
                <option value="API">API Only</option>
              </select>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter…"
                className="h-10 w-48 rounded-xl neo-inset border-none bg-transparent pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50 transition-all focus:w-64"
              />
            </div>
          </div>
        </div>

        {/* saved views */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5 shrink-0">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={cn(
                'rounded-lg px-2.5 py-1.5 text-sm font-medium transition',
                view === v.id ? 'neo text-[var(--color-primary)]' : 'text-muted-foreground hover:bg-muted hover:text-foreground hover:-translate-y-0.5',
              )}
            >
              {v.label}
            </button>
          ))}
          <span className="ml-auto font-mono text-[11px] text-muted-foreground">
            {rows.length} · keys: j/k move · x select · e assign me · ↵ open
          </span>
        </div>

        {/* table */}
        <div ref={tableContainerRef} className="mt-4 flex-1 min-h-0 overflow-y-auto neo rounded-[1.5rem] shadow-neo">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[var(--color-surface)] z-10">
              <tr className="border-b border-[var(--color-neo-border)] text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))}
                    className="accent-primary"
                  />
                </th>
                <th className="px-3 py-2.5 font-medium">Ref</th>
                <th className="px-3 py-2.5 font-medium">Source</th>
                <th className="px-3 py-2.5 font-medium">Subject</th>
                <th className="px-3 py-2.5 font-medium">Team</th>
                <th className="px-3 py-2.5 font-medium">Pri</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th 
                  className="px-3 py-2.5 font-medium cursor-pointer hover:text-foreground select-none transition-colors"
                  onClick={() => setSortBy(prev => prev === 'created-desc' ? 'created-asc' : 'created-desc')}
                  title="Click to sort by Created Date (Newest / Oldest)"
                >
                  <div className="flex items-center gap-1">
                    <span>Created</span>
                    {sortBy === 'created-desc' && <span className="text-[10px] text-primary">▼</span>}
                    {sortBy === 'created-asc' && <span className="text-[10px] text-primary">▲</span>}
                  </div>
                </th>
                <th className="px-3 py-2.5 font-medium">Assignee</th>
                <th className="px-3 py-2.5 font-medium">SLA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => {
                const breached = now > t.slaDueAt;
                return (
                  <tr
                    key={t.id}
                    id={`ticket-row-${t.id}`}
                    onClick={() => onOpenTicket(t)}
                    className={cn(
                      'cursor-pointer border-b border-[var(--color-neo-border)]/60 transition last:border-0',
                      i === active ? 'bg-primary/[0.06]' : 'hover:bg-[var(--color-surface)]/50 hover:shadow-neo',
                    )}
                  >
                    <td className="px-3 py-2.5" onClick={(e) => { e.stopPropagation(); toggle(t.id); }}>
                      <span className={cn('flex h-4 w-4 items-center justify-center rounded border', selected.has(t.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-input')}>
                        {selected.has(t.id) && <Check className="h-3 w-3" />}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[11px] text-muted-foreground">{t.ref}</td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {t.sourceType === 'SENTINEL' || t.source === 'Sentinel' ? (
                        <span className="inline-flex items-center gap-1 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-1.5 py-0.5 text-[10px] font-medium">
                          Sentinel
                        </span>
                      ) : t.sourceType === 'ADMIN_CREATED' ? (
                        <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium">
                          Admin
                        </span>
                      ) : t.source === 'Email' ? (
                        <span className="inline-flex items-center gap-1 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 px-1.5 py-0.5 text-[10px] font-medium">
                          <Mail className="h-3 w-3" /> Email
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium">
                          {t.sourceType === 'USER_CREATED' ? 'User' : (t.source || 'Portal')}
                        </span>
                      )}
                    </td>
                    <td className="max-w-sm px-3 py-2.5"><span className="line-clamp-1">{t.subject}</span></td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{t.team}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{t.priority}</td>
                    <td className="px-3 py-2.5"><span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{t.state}</span></td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground" title={`${new Date(t.createdAt).toLocaleString()} (${timeAgo(t.createdAt, now)})`}>
                      <span className="font-medium text-foreground/80">{formatCreatedDate(t.createdAt)}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">{t.assignee ?? <span className="text-xs italic text-muted-foreground">Unassigned</span>}</td>
                    <td className={cn('whitespace-nowrap px-3 py-2.5 text-xs font-medium', breached ? 'text-temp-ember' : 'text-muted-foreground')}>{untilLabel(t.slaDueAt, now)}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-12 text-center text-sm text-muted-foreground font-medium">
                    {view === 'pending-approvals' 
                      ? "No tickets are currently waiting for technician approval."
                      : "Nothing here."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* bulk action bar */}
      {ids.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center">
          <div className="pointer-events-auto flex items-center gap-2 neo rounded-[1.5rem] px-3 py-2 shadow-neo">
            <span className="px-1 text-sm font-medium">{ids.length} selected</span>
            <button onClick={() => bulkAssign(ids, me)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium transition hover:bg-muted">
              <UserCheck className="h-4 w-4" /> Assign to me
            </button>
            <button onClick={() => bulkStatus(ids, 'Resolved')} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium transition hover:bg-muted">
              <CircleCheck className="h-4 w-4 text-emerald-500" /> Resolve
            </button>
            <button onClick={() => bulkStatus(ids, 'Closed')} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium transition hover:bg-muted">
              <XCircle className="h-4 w-4" /> Close
            </button>
            <div className="relative">
              <button onClick={() => setMacroMenu((o) => !o)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium transition hover:bg-muted">
                <Zap className="h-4 w-4" /> Apply macro
              </button>
              {macroMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMacroMenu(false)} />
                  <div className="absolute bottom-11 left-0 z-20 w-64 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg">
                    {macros.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          bulkUpdate(ids, m.body);
                          if (m.setStatus) bulkStatus(ids, m.setStatus);
                          setSelected(new Set());
                          setMacroMenu(false);
                        }}
                        className="flex w-full flex-col items-start px-3 py-1.5 text-left transition hover:bg-muted"
                      >
                        <span className="text-sm font-medium">{m.name}{m.setStatus && <span className="ml-1 rounded bg-muted px-1 text-[10px] text-muted-foreground">→ {m.setStatus}</span>}</span>
                        <span className="line-clamp-1 text-[11px] text-muted-foreground">{m.body}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition hover:text-foreground">
              <X className="h-4 w-4" /> Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
